import { resetAnalytics } from '@parcel/analytics/browser';
import type { Session } from '@supabase/supabase-js';
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { clearSignupIntent } from './signup-intent.js';
import { isSupabaseConfigured, supabase } from './supabase.js';

const DEV_SESSION_STORAGE_KEY = 'parcel:dev-session';
const DEV_ACCESS_TOKEN = 'parcel-local-dev-token';

interface AuthState {
  session: Session | null;
  loading: boolean;
  devAuthAvailable: boolean;
  signInWithEmail: (
    email: string,
    options?: { redirectTo?: string },
  ) => Promise<{ error: Error | null }>;
  verifyEmailCode: (email: string, code: string) => Promise<{ error: Error | null }>;
  signInWithProvider: (
    provider: 'azure' | 'google',
    options?: { redirectTo?: string },
  ) => Promise<{ error: Error | null }>;
  signInLocally: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const devAuthAvailable = import.meta.env.DEV && !isSupabaseConfigured;

function getEmailRedirectTo(redirectTo?: string): string {
  if (redirectTo?.startsWith('/') && !redirectTo.startsWith('//')) {
    return `${window.location.origin}${redirectTo}`;
  }

  return window.location.origin;
}

function createDevSession(): Session {
  const now = Math.floor(Date.now() / 1000);
  return {
    access_token: DEV_ACCESS_TOKEN,
    token_type: 'bearer',
    expires_in: 60 * 60 * 24,
    expires_at: now + 60 * 60 * 24,
    refresh_token: 'parcel-local-dev-refresh-token',
    user: {
      id: 'local-dev-user',
      aud: 'authenticated',
      role: 'authenticated',
      email: 'local@parcel.dev',
      app_metadata: { provider: 'local-dev', providers: ['local-dev'] },
      user_metadata: { name: 'Local Preview' },
      created_at: new Date().toISOString(),
    },
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (devAuthAvailable && localStorage.getItem(DEV_SESSION_STORAGE_KEY) === 'true') {
      setSession(createDevSession());
      setLoading(false);
      return;
    }

    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const signInWithEmail = async (email: string, options?: { redirectTo?: string }) => {
    if (!isSupabaseConfigured) {
      return { error: new Error('Supabase auth is not configured for this local workspace.') };
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: getEmailRedirectTo(options?.redirectTo) },
    });
    return { error };
  };

  const verifyEmailCode = async (email: string, code: string) => {
    if (!isSupabaseConfigured) {
      return { error: new Error('Supabase auth is not configured for this local workspace.') };
    }

    const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' });
    return { error };
  };

  const signInWithProvider = async (
    provider: 'azure' | 'google',
    options?: { redirectTo?: string },
  ) => {
    if (!isSupabaseConfigured) {
      return { error: new Error('Supabase auth is not configured for this local workspace.') };
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: getEmailRedirectTo(options?.redirectTo),
        ...(provider === 'azure' ? { scopes: 'openid profile email' } : {}),
      },
    });
    return { error };
  };

  const signInLocally = () => {
    localStorage.setItem(DEV_SESSION_STORAGE_KEY, 'true');
    setSession(createDevSession());
  };

  const signOut = async () => {
    // Before the dev branch returns, and before the redirect, so the person is
    // unlinked either way.
    resetAnalytics();
    clearSignupIntent();

    if (session?.access_token === DEV_ACCESS_TOKEN) {
      localStorage.removeItem(DEV_SESSION_STORAGE_KEY);
      setSession(null);
      return;
    }

    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        loading,
        devAuthAvailable,
        signInWithEmail,
        verifyEmailCode,
        signInWithProvider,
        signInLocally,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
